"use client";

import Breadcrumbs from '@/components/Breadcrumbs';
import Link from 'next/link';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabaseClient';
import { 
    FaFilePdf, 
    FaYoutube, 
    FaShareNodes, 
    FaWhatsapp, 
    FaPhone, 
    FaEnvelope, 
    FaCompass, 
    FaInstagram, 
    FaArrowUp, 
    FaPlus, 
    FaXmark,
    FaBookOpen,
    FaWandSparkles
} from 'react-icons/fa6';

export interface UsageGuide {
    id: string;
    slug?: string;
    title_en: string;
    title_hi: string;
    description_en?: string;
    description_hi?: string;
    image_url: string;
    tags_en?: string;
    tags_hi?: string;
    guide_type: 'external_link' | 'html_content' | 'pdf_link' | 'youtube_link';
    external_url?: string;
    html_content_en?: string;
    html_content_hi?: string;
    display_order: number;
    is_active: boolean;
}

const SEED_GUIDES: UsageGuide[] = [
    {
        id: '12-jyotirlinga-chowki',
        title_en: '12 Jyotirlinga Chowki',
        title_hi: '१२ ज्योतिर्लिंग चौकी',
        description_en: 'Brings the divine presence and blessings of all 12 sacred Jyotirlingas into your home. A powerful medium for devotion and spiritual peace.',
        description_hi: 'आपके घर में सभी 12 पवित्र ज्योतिर्लिंगों की दिव्य उपस्थिति और आशीर्वाद लाता है। भक्ति और आध्यात्मिक शांति के लिए एक शक्तिशाली माध्यम।',
        image_url: 'https://admin.thefuture.university/_next/image?url=https%3A%2F%2Ftfu-media.s3.ap-south-1.amazonaws.com%2Fproducts%2F1760023060107%2F12-jyotirlinga-chowki-d98c6c-ChatGPT%2520Image%2520Oct%25209%252C%25202025%252C%252006_37_02%2520PM.webp&w=1920&q=75',
        tags_en: 'shiva, mahadev, jyotirlinga, 12, blessings, divine, spiritual, devotion, puja, chowki, temple, peace, energy',
        tags_hi: 'शिव, महादेव, ज्योतिर्लिंग, १२, आशीर्वाद, दिव्य, आध्यात्मिक, भक्ति, पूजा, चौकी, मंदिर, शांति, ऊर्जा',
        guide_type: 'pdf_link',
        external_url: 'https://www.canva.com/design/DAGzmCasR30/SoQqfsQa0L8-Brcud7lyVg/view?utm_content=DAGzmCasR30&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=hcae1e9e607',
        display_order: 1,
        is_active: true
    },
    {
        id: 'bagla-mukhi',
        title_en: 'Bagla Mukhi',
        title_hi: 'बगलामुखी',
        description_en: 'Brings victory, protection, and strength against obstacles.',
        description_hi: 'बाधाओं के खिलाफ विजय, सुरक्षा और शक्ति लाता है।',
        image_url: 'https://tfu-media.s3.ap-south-1.amazonaws.com/products/1759930148093/bagla-mukhi-kavach-73c597-Gemini_Generated_Image_stx2hnstx2hnstx2.webp',
        tags_en: 'victory, protection, strength, obstacles',
        tags_hi: 'बगलामुखी, विजय, सुरक्षा, शक्ति, बाधाओं',
        guide_type: 'youtube_link',
        external_url: 'https://youtu.be/tdAj4u2lKcE',
        display_order: 2,
        is_active: true
    },
    {
        id: 'vastu-kit',
        title_en: 'Vastu Kit',
        title_hi: 'वास्तु किट',
        description_en: 'Balances energies to create harmony & prosperity in your home.',
        description_hi: 'आपके घर में सद्भाव और समृद्धि बनाने के लिए ऊर्जा को संतुलित करता है।',
        image_url: 'https://tfu-media.s3.ap-south-1.amazonaws.com/products/1760093785582/ultimate-vastu-kit-72f921-636d303f-6f31-48aa-b786-52bfb9a0423d.webp',
        tags_en: 'vastu, harmony, prosperity, home, energies',
        tags_hi: 'वास्तु, सद्भाव, समृद्धि, घर, ऊर्जा',
        guide_type: 'youtube_link',
        external_url: 'https://youtu.be/tddGrFeHUJs',
        display_order: 3,
        is_active: true
    },
    {
        id: 'ganesh-shakti-kit',
        title_en: 'Ganesh Shakti Kit',
        title_hi: 'गणेश शक्ति किट',
        description_en: "Invokes Lord Ganesha's blessings for wisdom and success.",
        description_hi: 'ज्ञान और सफलता के लिए भगवान गणेश का आशीर्वाद प्राप्त करता है।',
        image_url: 'https://admin.thefuture.university/_next/image?url=https%3A%2F%2Ftfu-media.s3.ap-south-1.amazonaws.com%2Fproducts%2F1760863866237%2Fganesh-shakti-kit-032002-Gemini_Generated_Image_p8uso9p8uso9p8us.png&w=1920&q=75',
        tags_en: 'ganesha, wisdom, success, blessings',
        tags_hi: 'गणेश, बुद्धि, सफलता, आशीर्वाद',
        guide_type: 'youtube_link',
        external_url: 'https://youtu.be/DM7B0KaV_1I?si=yfnUrDmNL7yd17oA',
        display_order: 4,
        is_active: true
    },
    {
        id: 'mangal-shanti-kit',
        title_en: 'Mangal Shanti Kit',
        title_hi: 'मंगल शांति किट',
        description_en: 'Reduces the effects of Mangal dosha and enhances peace.',
        description_hi: 'मंगल दोष के प्रभाव को कम करता है और शांति बढ़ाता है।',
        image_url: 'https://admin.thefuture.university/_next/image?url=https%3A%2F%2Ftfu-media.s3.ap-south-1.amazonaws.com%2Fproducts%2F1760863979848%2Faghori-mangal-shanti-kit-7fd26e-Gemini_Generated_Image_j2o72mj2o72mj2o7.png&w=1920&q=75',
        tags_en: 'mangal dosha, peace, shanti, effects',
        tags_hi: 'मंगल दोष, शांति, प्रभाव',
        guide_type: 'youtube_link',
        external_url: 'https://youtube.com/shorts/ga0S7W-1i3w',
        display_order: 5,
        is_active: true
    },
    {
        id: 'vyapar-vruddhi-kit',
        title_en: 'Vyapar Vruddhi Kit',
        title_hi: 'व्यापार वृद्धि किट',
        description_en: 'Attracts wealth, growth, and success in business ventures.',
        description_hi: 'व्यापारिक उपक्रमों में धन, वृद्धि और सफलता को आकर्षित करता है।',
        image_url: 'https://admin.thefuture.university/_next/image?url=https%3A%2F%2Ftfu-media.s3.ap-south-1.amazonaws.com%2Fproducts%2F1760864026762%2Fvyapar-vruddhi-kit-ff1705-Gemini_Generated_Image_5gl5tj5gl5tj5gl5.png&w=1920&q=75',
        tags_en: 'business, wealth, growth, money, success, vyapar, vruddhi',
        tags_hi: 'व्यापार, वृद्धि, धन, सफलता',
        guide_type: 'youtube_link',
        external_url: 'https://youtu.be/0MQesD5pcQk',
        display_order: 6,
        is_active: true
    },
    {
        id: 'aghori-evil-eye-locket',
        title_en: 'Aghori Evil Eye Locket',
        title_hi: 'अघोरी बुरी नजर लॉकेट',
        description_en: 'Shields you from negativity and evil influences.',
        description_hi: 'आपको नकारात्मकता और बुरी शक्तियों से बचाता है।',
        image_url: 'https://tfu-media.s3.ap-south-1.amazonaws.com/products/1759680797340/aghori-evil-eye-locket-edb358-Gemini_Generated_Image_5j3vl25j3vl25j3v.webp',
        tags_en: 'evil eye, negativity, protection, shields, aghori',
        tags_hi: 'बुरी नजर, नकारात्मकता, सुरक्षा, कवच',
        guide_type: 'youtube_link',
        external_url: 'https://youtu.be/vNgW9dsJW2I',
        display_order: 7,
        is_active: true
    },
    {
        id: 'rudraksha-set',
        title_en: 'Rudraksha Set',
        title_hi: 'रुद्राक्ष सेट',
        description_en: 'Empowers your mind, body, and soul with sacred energy.',
        description_hi: 'आपके मन, शरीर और आत्मा को पवित्र ऊर्जा से सशक्त बनाता है।',
        image_url: 'https://admin.thefuture.university/_next/image?url=https%3A%2F%2Ftfu-media.s3.ap-south-1.amazonaws.com%2Fproducts%2F1760096235095%2Frudraksha-set-952e16-Gemini_Generated_Image_xxzji7xxzji7xxzj%2520%25281%2529.webp&w=1920&q=75',
        tags_en: 'rudraksha, sacred energy, mind, body, soul',
        tags_hi: 'रुद्राक्ष, पवित्र ऊर्जा, मन, शरीर, आत्मा',
        guide_type: 'pdf_link',
        external_url: 'https://www.canva.com/design/DAGrFe5jOTY/4RfpS877zJtx67V_88W-qA/view?utlId=hc4f44a65ff',
        display_order: 8,
        is_active: true
    },
    {
        id: 'kuber-yakshani-potli',
        title_en: 'Kuber Yakshani Potli',
        title_hi: 'कुबेर यक्षिणी पोटली',
        description_en: 'Opens pathways to fortune and success in every endeavor.',
        description_hi: 'हर प्रयास में भाग्य और सफलता के मार्ग खोलता है।',
        image_url: 'https://admin.thefuture.university/_next/image?url=https%3A%2F%2Ftfu-media.s3.ap-south-1.amazonaws.com%2Fproducts%2F1759681522495%2Fkuber-yakshani-potli-bf4d9b-WhatsApp%2520Image%25202025-10-05%2520at%252021.05.03%2520%25281%2529.jpg&w=1920&q=75',
        tags_en: 'kuber, fortune, success, endeavor, potli, money',
        tags_hi: 'कुबेर, भाग्य, सफलता, पोटली',
        guide_type: 'youtube_link',
        external_url: 'https://youtu.be/PcJtspaEkyM',
        display_order: 9,
        is_active: true
    },
    {
        id: 'gajlaxmi-sadhna-kit',
        title_en: 'GajLaxmi Sadhna Kit',
        title_hi: 'गजलक्ष्मी साधना किट',
        description_en: 'Invokes the combined blessings of Lord Ganesha and Goddess Lakshmi for immense wealth, success, and removal of obstacles.',
        description_hi: 'अपार धन, सफलता और बाधाओं को दूर करने के लिए भगवान गणेश और देवी लक्ष्मी का संयुक्त आशीर्वाद प्राप्त करें।',
        image_url: 'https://admin.thefuture.university/_next/image?url=https%3A%2F%2Ftfu-media.s3.ap-south-1.amazonaws.com%2Fproducts%2F1760255007948%2Fgajlaxmi-sadhna-kit-cb1fce-ganesh_lakshami_moorti_kit_hero_pic.webp&w=1920&q=75',
        tags_en: 'ganesha, lakshmi, gajlaxmi, wealth, prosperity, success, fortune, sadhna, diwali, blessings, finance',
        tags_hi: 'गणेश, लक्ष्मी, गजलक्ष्मी, धन, समृद्धि, सफलता, सौभाग्य, साधना, दिवाली, आशीर्वाद, वित्त',
        guide_type: 'pdf_link',
        external_url: 'https://www.canva.com/design/DAG1lIc-2TM/RRx-I2EmC4a0mmQApRzhhQ/view?utm_content=DAG1lIc-2TM&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h4e483b71a1',
        display_order: 10,
        is_active: true
    },
    {
        id: 'karungali-mala',
        title_en: 'Karungali Mala',
        title_hi: 'करुंगली माला',
        description_en: 'Enhances mental clarity, protection, and positive energy flow. Crafted from natural ebony wood for spiritual practice and meditation.',
        description_hi: 'मानसिक स्पष्टता, सुरक्षा और सकारात्मक ऊर्जा प्रवाह को बढ़ाता है। आध्यात्मिक अभ्यास और ध्यान के लिए प्राकृतिक करुंगली लकड़ी से निर्मित।',
        image_url: 'https://frwfsybeezicmriyttmf.supabase.co/storage/v1/object/public/product_images/product-images/karungali-mala.png',
        tags_en: 'karungali, mala, ebony, ebony wood, protection, focus, energy, positivity, rahu, mars, spiritual',
        tags_hi: 'करुंगली, माला, एबोनी, सुरक्षा, ऊर्जा, सकारात्मकता, राहु, मंगल, आध्यात्मिक',
        guide_type: 'external_link',
        external_url: 'https://canva.link/hs59bz2auoznbwv',
        display_order: 11,
        is_active: true
    },
    {
        id: 'kuber-kripa-mahavidya-kit',
        title_en: 'Kuber Kripa Mahavidya Kit',
        title_hi: 'कुबेर कृपा महाविद्या किट',
        description_en: 'Sacred Sadhna kit designed to attract wealth, divine prosperity, and abundance through sacred Yantras and cosmic energies.',
        description_hi: 'पवित्र यंत्रों और ब्रह्मांडीय ऊर्जाओं के माध्यम से धन, दिव्य समृद्धि और प्रचुरता को आकर्षित करने के लिए डिज़ाइन की गई पवित्र साधना किट।',
        image_url: 'https://frwfsybeezicmriyttmf.supabase.co/storage/v1/object/public/product_images/product-images/kuber-kripa-mahavidya-kit.jpg',
        tags_en: 'kuber, kripa, mahavidya, kit, wealth, prosperity, abundance, yantra, shri yantra, shani yantra, sadhana, fortune, blessings',
        tags_hi: 'कुबेर, कृपा, महाविद्या, किट, धन, समृद्धि, प्रचुरता, यंत्र, श्री यंत्र, शनि यंत्र, साधना, भाग्य, आशीर्वाद',
        guide_type: 'external_link',
        external_url: 'https://canva.link/rp3j6u8zy02uzbu',
        display_order: 12,
        is_active: true
    }
];

// High-Performance Dynamic HTML5 Star Particle Canvas Component
const StarCanvas: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animationFrameId: number;
        let width = (canvas.width = window.innerWidth);
        let height = (canvas.height = window.innerHeight);

        const handleResize = () => {
            if (!canvas) return;
            width = canvas.width = window.innerWidth;
            height = canvas.height = window.innerHeight;
        };

        window.addEventListener('resize', handleResize);

        const numStars = 250;
        const stars = Array.from({ length: numStars }).map(() => ({
            x: Math.random() * width,
            y: Math.random() * height,
            radius: Math.random() * 1.5 + 0.4,
            color: ['#ffffff'][Math.floor(Math.random() * 5)],
            speedY: Math.random() * 0.12 + 0.03,
            speedX: (Math.random() - 0.5) * 0.05,
            opacity: Math.random() * 0.45 + 0.08,
            opacitySpeed: (Math.random() * 0.004 + 0.001) * (Math.random() > 0.5 ? 1 : -1)
        }));

        const render = () => {
            ctx.clearRect(0, 0, width, height);

            stars.forEach(star => {
                star.y -= star.speedY;
                star.x += star.speedX;
                star.opacity += star.opacitySpeed;

                if (star.opacity >= 0.45 || star.opacity <= 0.08) {
                    star.opacitySpeed = -star.opacitySpeed;
                }

                if (star.y < 0) star.y = height;
                if (star.x < 0) star.x = width;
                if (star.x > width) star.x = 0;

                ctx.save();
                ctx.globalAlpha = Math.max(0.08, Math.min(0.45, star.opacity));
                ctx.fillStyle = star.color;
                ctx.shadowBlur = star.radius > 1.2 ? 4 : 1;
                ctx.shadowColor = star.color;
                ctx.beginPath();
                ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
            });

            animationFrameId = requestAnimationFrame(render);
        };

        render();

        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                pointerEvents: 'none',
                zIndex: 0
            }}
        />
    );
};

export default function ProductUsageClient() {
    const [guides, setGuides] = useState<UsageGuide[]>(SEED_GUIDES);
    const [searchQuery, setSearchQuery] = useState('');
    const [lang, setLang] = useState<'en' | 'hi'>('en');
    const [isScrolled, setIsScrolled] = useState(false);
    const [fabActive, setFabActive] = useState(false);
    const [selectedModalGuide, setSelectedModalGuide] = useState<UsageGuide | null>(null);
    const [socialHandles, setSocialHandles] = useState({
        instagram_url: 'https://www.instagram.com/astrojiofficial',
        youtube_url: 'https://www.youtube.com/@astrolearn_app',
        whatsapp_group_url: 'https://chat.whatsapp.com/KTu3RlGXcx53WnjDunoRoF?mode=wwt',
        support_phone: '7973938627',
        support_email: 'support@thefuture.university'
    });

    // Fetch live social handles from database
    useEffect(() => {
        const fetchSocialHandles = async () => {
            try {
                const { data, error } = await supabase
                    .from('website_social_handles')
                    .select('*')
                    .eq('id', 'default')
                    .maybeSingle();

                if (!error && data) {
                    setSocialHandles({
                        instagram_url: data.instagram_url || 'https://www.instagram.com/astrojiofficial',
                        youtube_url: data.youtube_url || 'https://www.youtube.com/@astrolearn_app',
                        whatsapp_group_url: data.whatsapp_group_url || 'https://chat.whatsapp.com/KTu3RlGXcx53WnjDunoRoF?mode=wwt',
                        support_phone: data.support_phone || '7973938627',
                        support_email: data.support_email || 'support@thefuture.university'
                    });
                }
            } catch (err) {
                console.error('Error fetching social handles:', err);
            }
        };

        fetchSocialHandles();
    }, []);

    // Fetch live guides from database
    useEffect(() => {
        const fetchGuides = async () => {
            try {
                const { data, error } = await supabase
                    .from('product_usage_guides')
                    .select('*')
                    .eq('is_active', true)
                    .order('display_order', { ascending: true });

                if (!error && data && data.length > 0) {
                    setGuides(data as UsageGuide[]);
                }
            } catch (err) {
                console.error('Error fetching product usage guides:', err);
            }
        };

        fetchGuides();
    }, []);

    // Handle scroll for sticky navbar state
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 180) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTopSmooth = () => {
        const duration = 600; // ms
        const startPosition = window.pageYOffset || document.documentElement.scrollTop;
        if (startPosition === 0) return;
        const startTime = performance.now();

        const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

        const animateScroll = (currentTime: number) => {
            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);
            const easeProgress = easeOutCubic(progress);

            window.scrollTo(0, startPosition * (1 - easeProgress));

            if (progress < 1) {
                requestAnimationFrame(animateScroll);
            }
        };

        requestAnimationFrame(animateScroll);
    };

    const filteredGuides = useMemo(() => {
        if (!searchQuery.trim()) return guides;
        const q = searchQuery.toLowerCase().trim();
        return guides.filter(guide => 
            (guide.title_en || '').toLowerCase().includes(q) ||
            (guide.title_hi || '').toLowerCase().includes(q) ||
            (guide.description_en || '').toLowerCase().includes(q) ||
            (guide.description_hi || '').toLowerCase().includes(q) ||
            (guide.tags_en || '').toLowerCase().includes(q) ||
            (guide.tags_hi || '').toLowerCase().includes(q)
        );
    }, [guides, searchQuery]);

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Astroji Product Usage Guide',
                    text: 'Explore authentic usage guides and rituals for Astroji spiritual products.',
                    url: window.location.href,
                });
            } catch (err) {}
        } else {
            await navigator.clipboard.writeText(window.location.href);
            alert('Guide link copied to clipboard!');
        }
    };

    const handleGuideClick = (guide: UsageGuide, e: React.MouseEvent) => {
        if (guide.guide_type === 'html_content' || guide.html_content_en || guide.html_content_hi) {
            e.preventDefault();
            setSelectedModalGuide(guide);
        }
    };

    const activeHtmlContent = useMemo(() => {
        if (!selectedModalGuide) return '';
        const rawContent = lang === 'hi'
            ? (selectedModalGuide.html_content_hi || selectedModalGuide.html_content_en || '<p style="color:white;text-align:center;padding:20px;">सामग्री लोड हो रही है...</p>')
            : (selectedModalGuide.html_content_en || selectedModalGuide.html_content_hi || '<p style="color:white;text-align:center;padding:20px;">Content loading...</p>');

        return DOMPurify.sanitize(rawContent, {
            ADD_TAGS: ['style', 'iframe', 'link', 'meta'],
            ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'scrolling', 'style', 'class', 'id'],
            WHOLE_DOCUMENT: true
        });
    }, [selectedModalGuide, lang]);

    // Framer Motion Animation Variants
    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.08
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 40, scale: 0.94 },
        show: { 
            opacity: 1, 
            y: 0, 
            scale: 1,
            transition: {
                type: 'spring' as const,
                stiffness: 260,
                damping: 20
            }
        }
    };

    return (
        <div className={`usage-page-wrapper ${lang === 'hi' ? 'lang-hi' : 'lang-en'}`}>
            {/* HTML5 Canvas Moving Starfield */}
            <StarCanvas />

            <div className="usage-content-layer">
                {/* Hero Header with Framer Motion */}
                <motion.header 
                    className="hero"
                    initial={{ opacity: 0, y: -30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    <Link href="/" title="Go to AstroJi Homepage" className="inline-block cursor-pointer">
                        <motion.img 
                            src="https://frwfsybeezicmriyttmf.supabase.co/storage/v1/object/public/astroji-01/AstroJi%20Logo%20(1).png" 
                            alt="Astroji Logo" 
                            className="hero-logo"
                            animate={{ 
                                scale: [1, 1.08, 1],
                                boxShadow: [
                                    "0 0 20px rgba(242, 134, 44, 0.4), 0 0 40px rgba(255, 215, 0, 0.2)",
                                    "0 0 40px rgba(242, 134, 44, 0.8), 0 0 70px rgba(255, 215, 0, 0.4)",
                                    "0 0 20px rgba(242, 134, 44, 0.4), 0 0 40px rgba(255, 215, 0, 0.2)"
                                ]
                            }}
                            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                        />
                    </Link>
                    <motion.h1
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    >
                        {lang === 'en' ? 'Astroji Product Usage Guide' : 'एस्ट्रोजी उत्पाद उपयोग मार्गदर्शिका'}
                    </motion.h1>
                </motion.header>

                {/* Floating Glass Sticky Navbar with Framer Motion */}
                <motion.div 
                    className="sticky-nav-wrapper"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                >
                    <nav className="sticky-nav">
                        <Link 
                            href="/" 
                            className="nav-logo-container transition-transform hover:scale-105"
                        >
                            <motion.img 
                                src="https://frwfsybeezicmriyttmf.supabase.co/storage/v1/object/public/astroji-01/AstroJi%20Logo%20(1).png" 
                                alt="Astroji Logo" 
                                className="nav-logo"
                                whileHover={{ rotate: 360 }}
                                transition={{ duration: 0.6 }}
                            />
                            <span className="nav-title">Astroji</span>
                        </Link>

                        <div className="search-box">
                            <input 
                                type="search" 
                                className="search-input"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder={lang === 'en' ? "Search products... (e.g., 'protection', 'शांति')" : "उत्पाद खोजें... (जैसे, 'सुरक्षा', 'शांति')"}
                            />
                        </div>

                        <motion.button 
                            className="lang-switcher" 
                            onClick={() => setLang(prev => prev === 'en' ? 'hi' : 'en')}
                            whileHover={{ scale: 1.08 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {lang === 'en' ? 'हिन्दी' : 'English'}
                        </motion.button>

                        <motion.button 
                            className="share-btn" 
                            onClick={handleShare}
                            whileHover={{ scale: 1.15, rotate: 15 }}
                            whileTap={{ scale: 0.9 }}
                            title="Share this Guide"
                        >
                            <FaShareNodes />
                        </motion.button>
                    </nav>
                </motion.div>

                {/* Main Product Grid with Framer Motion Stagger */}
                <div className="usage-container">
                    <Breadcrumbs items={[{ label: 'Product Usage & Rituals' }]} variant="dark" className="mb-4 justify-start" />
                    <motion.main 
                        className="product-grid"
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                    >
                        {filteredGuides.length === 0 ? (
                            <motion.div 
                                style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '50px 20px', color: 'var(--accent-color)', fontSize: '1.2rem' }}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                            >
                                {lang === 'en' ? 'No products found matching your search.' : 'आपकी खोज से मेल खाने वाला कोई उत्पाद नहीं मिला।'}
                            </motion.div>
                        ) : (
                            filteredGuides.map((guide) => {
                                const isHtmlModal = guide.guide_type === 'html_content' || Boolean(guide.html_content_en || guide.html_content_hi);
                                const isPdfOrCanva = guide.guide_type === 'pdf_link' || (guide.external_url && guide.external_url.includes('canva'));
                                const title = lang === 'hi' ? (guide.title_hi || guide.title_en) : guide.title_en;
                                const description = lang === 'hi' ? (guide.description_hi || guide.description_en) : (guide.description_en || guide.description_hi);

                                return (
                                    <motion.article 
                                        key={guide.id || guide.slug} 
                                        className="product-card"
                                        variants={cardVariants}
                                        whileHover={{ 
                                            y: -16, 
                                            scale: 1.03,
                                            boxShadow: "0 24px 50px rgba(0,0,0,0.65), 0 0 35px rgba(242, 134, 44, 0.45)",
                                            transition: { type: "spring" as const, stiffness: 400, damping: 22 }
                                        }}
                                    >
                                        <div className="product-image-container">
                                            <motion.img 
                                                src={guide.image_url} 
                                                alt={title} 
                                                loading="lazy" 
                                                whileHover={{ scale: 1.12 }}
                                                transition={{ duration: 0.4 }}
                                            />
                                        </div>
                                        <h2>{title}</h2>
                                        <p>{description}</p>

                                        {isHtmlModal ? (
                                            <motion.button 
                                                onClick={(e) => handleGuideClick(guide, e)}
                                                className="cta-button pdf"
                                                whileHover={{ scale: 1.08 }}
                                                whileTap={{ scale: 0.94 }}
                                            >
                                                <FaBookOpen />
                                                <span>{lang === 'en' ? 'Open Guide' : 'यहाँ खोलें'}</span>
                                            </motion.button>
                                        ) : (
                                            <motion.a 
                                                href={guide.external_url || '#'} 
                                                target="_blank" 
                                                rel="noopener noreferrer" 
                                                className={`cta-button ${isPdfOrCanva ? 'pdf' : ''}`}
                                                whileHover={{ scale: 1.08 }}
                                                whileTap={{ scale: 0.94 }}
                                            >
                                                {guide.guide_type === 'youtube_link' ? <FaYoutube style={{ fontSize: '1.2rem' }} /> : <FaFilePdf />}
                                                <span>{lang === 'en' ? (guide.guide_type === 'youtube_link' ? 'Watch Video' : 'Open Here') : (guide.guide_type === 'youtube_link' ? 'वीडियो देखें' : 'यहाँ खोलें')}</span>
                                            </motion.a>
                                        )}
                                    </motion.article>
                                );
                            })
                        )}
                    </motion.main>
                </div>

                {/* WhatsApp Inner Circle Community Card with Framer Motion Floating Loop */}
                <div className="usage-container">
                    <motion.div 
                        className="community-card"
                        animate={{ y: [-4, 6, -4] }}
                        transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut" }}
                        whileHover={{ scale: 1.02 }}
                    >
                        <motion.div
                            animate={{ scale: [1, 1.15, 1] }}
                            transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
                        >
                            <FaWhatsapp className="whatsapp-icon" />
                        </motion.div>
                        <h3>
                            {lang === 'en' ? 'JOIN THE ASTROJI INNER CIRCLE' : 'एस्ट्रोजी इनर सर्कल से जुड़ें'}
                        </h3>
                        <p>
                            {lang === 'en' 
                                ? 'Get exclusive spiritual tips, early access to new products, and festival guidance from our experts.'
                                : 'हमारे विशेषज्ञों से विशेष आध्यात्मिक टिप्स, नए उत्पादों तक शीघ्र पहुंच और त्योहारों पर मार्गदर्शन प्राप्त करें।'}
                        </p>
                        <motion.a 
                            href={socialHandles.whatsapp_group_url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="cta-button"
                            whileHover={{ scale: 1.08 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <FaWandSparkles />
                            <span>{lang === 'en' ? 'Join for Free' : 'मुफ़्त में जुड़ें'}</span>
                        </motion.a>
                    </motion.div>
                </div>

                {/* Footer Section */}
                <footer className="page-footer">
                    <div className="usage-container">
                        <div className="footer-container">

                            {/* Column 2: Support & Info Links */}
                            <div className="footer-column links">
                                <h3>
                                    {lang === 'en' ? 'Support & Info' : 'सहायता और जानकारी'}
                                </h3>
                                <ul>
                                    <li className="cta-item">
                                        <a href="https://astroji.com/products" target="_blank" rel="noopener noreferrer">
                                            <FaCompass />
                                            <span>{lang === 'en' ? 'Explore All Products' : 'सभी उत्पाद देखें'}</span>
                                        </a>
                                    </li>
                                    <li>
                                        <a href={`tel:${socialHandles.support_phone}`}>
                                            <FaPhone /> <span>{socialHandles.support_phone}</span>
                                        </a>
                                    </li>
                                    <li>
                                        <a href={`mailto:${socialHandles.support_email}`}>
                                            <FaEnvelope /> <span>{socialHandles.support_email}</span>
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            {/* Column 3: Social Connections */}
                            <div className="footer-column">
                                <h3>
                                    {lang === 'en' ? 'Connect With Us' : 'हमसे जुड़ें'}
                                </h3>
                                <div className="social-links">
                                    <a href={socialHandles.youtube_url} target="_blank" rel="noopener noreferrer" title="YouTube"><FaYoutube /></a>
                                    <a href={socialHandles.instagram_url} target="_blank" rel="noopener noreferrer" title="Instagram"><FaInstagram /></a>
                                    <a href={socialHandles.whatsapp_group_url} target="_blank" rel="noopener noreferrer" title="WhatsApp"><FaWhatsapp /></a>
                                </div>
                            </div>
                        </div>

                        <div className="footer-bottom">
                            <p>© {new Date().getFullYear()} Astroji. All Rights Reserved.</p>
                        </div>
                    </div>
                </footer>
            </div>

            {/* Floating Action Button (FAB) with Framer Motion */}
            <div className="fab-container">
                <AnimatePresence>
                    {fabActive && (
                        <motion.div 
                            className="fab-options"
                            initial={{ opacity: 0, y: 15, scale: 0.8 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 15, scale: 0.8 }}
                            transition={{ duration: 0.25 }}
                        >
                            <motion.a 
                                href={socialHandles.youtube_url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="fab-option fab-youtube" 
                                title="YouTube"
                                whileHover={{ scale: 1.25 }}
                            >
                                <FaYoutube />
                            </motion.a>
                            <motion.a 
                                href={socialHandles.instagram_url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="fab-option fab-instagram" 
                                title="Instagram"
                                whileHover={{ scale: 1.25 }}
                            >
                                <FaInstagram />
                            </motion.a>
                            <motion.a 
                                href={socialHandles.whatsapp_group_url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="fab-option fab-whatsapp" 
                                title="Join Community"
                                whileHover={{ scale: 1.25 }}
                            >
                                <FaWhatsapp />
                            </motion.a>
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.div 
                    className={`fab-main-button ${fabActive ? 'active' : ''}`}
                    onClick={() => {
                        if (isScrolled && !fabActive) {
                            scrollToTopSmooth();
                        } else {
                            setFabActive(!fabActive);
                        }
                    }}
                    animate={{
                        rotate: fabActive ? 360 : [0, -18, 18, -12, 12, 0],
                        scale: [1, 1.15, 1.15, 1.08, 1.08, 1]
                    }}
                    transition={{
                        rotate: fabActive ? { duration: 0.4 } : { repeat: Infinity, repeatDelay: 4, duration: 0.8 },
                        scale: fabActive ? { duration: 0.2 } : { repeat: Infinity, repeatDelay: 4, duration: 0.8 }
                    }}
                    whileHover={{ scale: 1.18 }}
                    whileTap={{ scale: 0.92 }}
                >
                    {fabActive ? <FaXmark /> : isScrolled ? <FaArrowUp /> : <FaPlus />}
                </motion.div>
            </div>

            {/* HTML MODAL POPUP WITH FRAMER MOTION ANIMATEPRESENCE */}
            <AnimatePresence>
                {selectedModalGuide && (
                    <motion.div 
                        className="guide-modal-backdrop" 
                        onClick={() => setSelectedModalGuide(null)}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <motion.div 
                            className="guide-modal-box" 
                            onClick={(e) => e.stopPropagation()}
                            initial={{ opacity: 0, scale: 0.85, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.85, y: 30 }}
                            transition={{ type: "spring" as const, stiffness: 300, damping: 25 }}
                        >
                            <div className="guide-modal-header">
                                <h3>
                                    {lang === 'hi' ? (selectedModalGuide.title_hi || selectedModalGuide.title_en) : selectedModalGuide.title_en}
                                </h3>
                                <motion.button 
                                    className="guide-modal-close" 
                                    onClick={() => setSelectedModalGuide(null)}
                                    whileHover={{ scale: 1.15, rotate: 90 }}
                                    whileTap={{ scale: 0.9 }}
                                >
                                    <FaXmark />
                                </motion.button>
                            </div>

                            <div className="guide-modal-body">
                                <iframe 
                                    className="guide-modal-iframe"
                                    title={selectedModalGuide.title_en}
                                    srcDoc={activeHtmlContent}
                                />
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
